import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, Divider, TextField, Select, MenuItem, Typography, Tooltip, IconButton, CardMedia, CardContent } from '@mui/material';
import { Apps, ArrowForwardIos } from '@mui/icons-material';
import Links from '../components/widgets/Links';
import { useGetProfileQuery, useGetPublishedItemsQuery, useAddToLibraryMutation } from '../store/docApi';

const defaultTheme = { primary: '#2196f3', primaryText: 'white', bannerBackground: 'ghostwhite' };

export default function PublicProfile() {
  const navigate = useNavigate();
  const params = useParams();
  const { data: profile } = useGetProfileQuery({ pk: params.pk });
  const { data: products } = useGetPublishedItemsQuery({ pk: params.pk });
  const [addToLibrary] = useAddToLibraryMutation();
  const [theme, setTheme] = useState(defaultTheme);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('Newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState(['All']);
  const sortOptions = ['Newest', 'Popular', 'Price: Low to High', 'Price: High to Low'];
  const [viewMode, setViewMode] = useState('products');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState();
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (profile) {
      profile.categories && setCategories(['All', ...profile.categories]);
      if (profile.theme) {
        setTheme({ ...defaultTheme, ...profile.theme });
      }
    }
  }, [profile]);

  useEffect(() => {
    if (products) {
      const _filteredProducts = products.filter((product) => {
        const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
        const matchesSearch =
          searchQuery === '' ||
          (product.name && product.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
      });
      setFilteredProducts(_filteredProducts);
    }
  }, [products, selectedCategory, searchQuery]);

  useEffect(() => {
    if (products && params.id) {
      const product = products.find((product) => product.id === params.id);
      setSelectedProduct(product);
    } else {
      setSelectedProduct(null);
    }
  }, [params.id, products]);

  async function handleAddToCart(product) {
    await addToLibrary({ pk: product.pk, id: product.id });
  }

  const handleProductClick = (product) => {
    navigate(`/explore/${product.pk}/${product.id}`);
    setSelectedProduct(product);
  };

  const handleShowAllProducts = () => {
    setViewMode('products');
    setSelectedProduct(null);
    navigate(`/explore/${profile.pk}`);
  };

  const highlightText = (text, searchTerm) => {
    if (!text) return '';
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => (
      <span key={index} style={part.toLowerCase() === searchTerm.toLowerCase() ? { backgroundColor: '#FFE3B3', borderRadius: '2px' } : {}}>
        {part}
      </span>
    ));
  };

  const dynamicStyles = {
    activeTabButton: { borderBottom: `3px solid ${theme.primary}`, borderRadius: 0 },
    priceButton: {
      backgroundColor: theme.primary,
      color: theme.primaryText
    }
  };

  const getPrice = (product) => {
    return product.priceAmount ? `$${(product.priceAmount / 100).toFixed(2)}` : '';
  };

  const ProductDetails = ({ product }) => {
    if (!product) return null;
    return (
      <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: 350 }}>
        {/* Header: Show all products, arrow, product name */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 2, pb: 0 }}>
          <Tooltip title="Show all products">
            <IconButton onClick={handleShowAllProducts} sx={{ color: theme.primary }}>
              <Apps sx={{ color: theme.primary }} />
            </IconButton>
          </Tooltip>
          <ArrowForwardIos sx={{ mr: 1 }} />
          <Typography>{selectedProduct.name}</Typography>
        </Box>
        {/* Main content: images and details */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%' }}>
          {/* Left: Images */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minWidth: { md: 320 }, maxWidth: { md: 340 }, width: '100%' }}>
            {(product.pictures || product.picture) && (
              <CardMedia
                component="img"
                image={product.pictures ? product.pictures[selectedImage] : product.picture}
                alt={product.name}
                sx={{ width: 300, height: 300, objectFit: 'contain', bgcolor: '#f5f8fa', borderRadius: 1, mb: 2, maxWidth: '100%' }}
              />
            )}
            {/* Thumbnails below main image */}
            {product.pictures && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {product.pictures.map((img, index) => (
                  <CardMedia
                    key={index}
                    component="img"
                    image={img}
                    alt={`Thumbnail ${index + 1}`}
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      cursor: 'pointer',
                      border: selectedImage === index ? `2px solid ${theme.primary}` : '2px solid transparent',
                      borderRadius: 1
                    }}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </Box>
            )}
          </Box>
          {/* Right: Details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 2, width: '100%', pt: { xs: 0, md: 2 } }}>
            <CardContent sx={{ flexGrow: 1, p: 0 }}>
              <Typography variant="h5">{product.name}</Typography>
              <Typography variant="body2">{product.shopName}</Typography>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{getPrice(product)}</Typography>
              <Typography sx={{ mt: 2 }} variant="body2">{product.description}</Typography>
              <Typography sx={{ mt: 2 }} variant="body2">{highlightText(product.note, searchQuery)}</Typography>
            </CardContent>
            {/* Add to Library button at the bottom */}
            <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <Button
                style={dynamicStyles.priceButton}
                onClick={() => handleAddToCart(product)}
              >
                Add to Library
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  const ProductsGrid = () => {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
        {filteredProducts.map((product) => (
          <Card
            key={product.id}
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Box
              onClick={() => handleProductClick(product)}
              sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
            >
              {product.picture && (
                <CardMedia
                  component="img"
                  image={product.picture}
                  alt={product.name}
                  sx={{ height: 180, objectFit: 'cover' }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">{highlightText(product.name, searchQuery)}</Typography>
                <Typography variant="body2">{highlightText(product.description, searchQuery)}</Typography>
              </CardContent>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, mt: 'auto' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{getPrice(product)}</Typography>
              <Button
                style={dynamicStyles.priceButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(product);
                }}
              >
                Add to Library
              </Button>
            </Box>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    profile && (
      <Box>
        <Box>
          {profile.bannerImage ? (
            <img src={profile.bannerImage} alt="Banner" style={styles.bannerImage} />
          ) : (
            <Box sx={{ height: '100px', backgroundColor: theme.bannerBackground }} />
          )}
          <Box sx={{ mt: -6, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
              {profile.profileImage && <img src={profile.profileImage} alt={profile.name} style={styles.profileImage} />}
              <Box>
                <Typography variant="h4">{profile.name}</Typography>
                {profile.designer && <Typography>{profile.designer}</Typography>}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box>
            <Button variant="text" onClick={handleShowAllProducts} sx={viewMode === 'products' ? dynamicStyles.activeTabButton : {}}>
              Products
            </Button>
            <Button variant="text" onClick={() => setViewMode('about')} sx={viewMode === 'about' ? dynamicStyles.activeTabButton : {}}>
              About
            </Button>
          </Box>
          {viewMode === 'about' && (
            <Card sx={{ mt: 2 }}>
              <Typography variant="h5">About</Typography>
              <Typography>{profile.description}</Typography>
              <Divider sx={{ my: 2 }} />
              <Links links={profile.links} vertical={false} iconColor={theme.primary} />
            </Card>
          )}
          {viewMode === 'products' && (
            <Box>
              {selectedProduct ? (
                <ProductDetails product={selectedProduct} />
              ) : (
                <Box>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 2,
                      alignItems: 'center',
                      p: 2,
                      mt: 2
                    }}
                  >
                    <TextField
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} size="small">
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                    <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} size="small">
                      {sortOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </Card>
                  <Box sx={{ mt: 2 }}>
                    {products && products.length > 0 ? <ProductsGrid /> : <Typography>No products found</Typography>}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    )
  );
}

const styles = {
  bannerImage: {
    display: 'inline-flex',
    objectFit: 'cover',
    width: '100%',
    height: '100px'
  },
  profileImage: {
    display: 'inline-flex',
    objectFit: 'cover',
    width: '100px',
    height: '100px',
    borderRadius: '8px',
    border: '4px solid white'
  },
  productImage: { marginBottom: '12px', borderRadius: '4px' },
  price: { fontSize: 16, fontWeight: 500 }
};
